import { useEffect, useMemo, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Box } from '@cbhq/cds-web/layout/Box';
import { Pagination } from '@cbhq/cds-web/pagination/Pagination';
import { Table, TableBody, TableCell, TableHeader, TableRow } from '@cbhq/cds-web/tables';
import { Text } from '@cbhq/cds-web/typography/Text';

import type { Column, UserCellMember } from '../types';

import { UserCell } from './UserCell';

const DEFAULT_ITEMS_PER_PAGE = 10;
const tableStyle: React.CSSProperties = { width: '100%', minWidth: 0 };
const TEAM_TABLE_MIN_HEIGHT = 780;
const containerStyle: React.CSSProperties = { flex: 1, minHeight: TEAM_TABLE_MIN_HEIGHT } as const;
const paginationBoxStyle: React.CSSProperties = {
  marginTop: 'auto',
  marginBottom: 36,
};

const messages = defineMessages({
  colName: {
    id: 'teamTable.colName',
    defaultMessage: 'Name',
    description: 'Column header for member name',
  },
  colEntity: {
    id: 'teamTable.colEntity',
    defaultMessage: 'Entity',
    description: 'Column header for entity',
  },
  colProduct: {
    id: 'teamTable.colProduct',
    defaultMessage: 'Product',
    description: 'Column header for product (org tab)',
  },
  colProductAccess: {
    id: 'teamTable.colProductAccess',
    defaultMessage: 'Product access',
    description: 'Column header for product (org tab) when the Workspace role has its own column',
  },
  colWorkspaceRole: {
    id: 'teamTable.colWorkspaceRole',
    defaultMessage: 'Workspace role',
    description: 'Column header for the member Workspace role (org tab)',
  },
  colStatus: {
    id: 'teamTable.colStatus',
    defaultMessage: 'Status',
    description: 'Column header for member status',
  },
  colLastOnline: {
    id: 'teamTable.colLastOnline',
    defaultMessage: 'Last online',
    description: 'Column header for last online date',
  },
  previousPage: {
    id: 'teamTable.previousPage',
    defaultMessage: 'Previous page',
    description: 'Accessibility label for previous page button',
  },
  nextPage: {
    id: 'teamTable.nextPage',
    defaultMessage: 'Next page',
    description: 'Accessibility label for next page button',
  },
  tableName: {
    id: 'teamTable.tableName',
    defaultMessage: 'Team members',
    description: 'Accessible label for the team members table',
  },
});

const tableCellWhitespaceStyle = { paddingY: 0, paddingX: 3 } as const;

// Product Access and Workspace role hold short content (icons / one-word labels),
// so they render at 75% of the width of the other (unconstrained) columns.
const NARROW_COLUMN_WIDTH = 180;
const NARROW_COLUMNS = new Set<Column>(['product', 'workspaceRole']);
const firstTableCellWhitespaceStyle = { paddingY: 0, paddingX: 0, paddingStart: 2 } as const;
const lastTableCellWhitespaceStyle = { paddingY: 0, paddingEnd: 3, paddingStart: 2 } as const;

export type TeamTableProps = {
  /** Pre-filtered, pre-sorted members to display */
  members: UserCellMember[];
  orgName: string;
  columns: Column[];
  paginationResetKey?: string;
  /** Emails of members whose rows are animating out (opacity → 0) */
  removingMemberEmails?: Set<string>;
  itemsPerPage?: number;
  onNameClick: (member: UserCellMember) => void;
  /** Opens a pending invite row. Pending rows aren't clickable without it. */
  onPendingInviteClick?: (member: UserCellMember) => void;
  onRemoveFromOrg: (member: UserCellMember) => void;
  onCancelInvite?: (member: UserCellMember) => void;
  canCancelInvite?: (member: UserCellMember) => boolean;
  /** "Revoke admin access" for members with pending roles (see UserCellMember.pendingRoles) */
  onCancelPendingRole?: (member: UserCellMember) => void;
  canCancelPendingRole?: (member: UserCellMember) => boolean;
  showActions?: boolean;
  /** Whether the current user has permission to remove members from the org */
  canRemove?: boolean;
  /** Whether the current user has permission to edit member access */
  canEdit?: boolean;
};

export function TeamTable({
  members,
  orgName,
  columns,
  paginationResetKey,
  removingMemberEmails,
  itemsPerPage = DEFAULT_ITEMS_PER_PAGE,
  onNameClick,
  onPendingInviteClick,
  onRemoveFromOrg,
  onCancelInvite,
  canCancelInvite,
  onCancelPendingRole,
  canCancelPendingRole,
  showActions = true,
  canRemove = false,
  canEdit = false,
}: TeamTableProps) {
  const { formatMessage } = useIntl();
  const [currentPage, setCurrentPage] = useState(1);
  const previousResetKeyRef = useRef(paginationResetKey);
  const totalPages = Math.max(1, Math.ceil(members.length / itemsPerPage));
  const activePage = Math.min(currentPage, totalPages);

  const accessibilityLabels = useMemo(
    () => ({
      previous: formatMessage(messages.previousPage),
      next: formatMessage(messages.nextPage),
    }),
    [formatMessage],
  );

  // Reset pagination for filter/search changes. Otherwise keep the current page
  // unless the current page no longer exists after the list size changes.
  useEffect(() => {
    const shouldReset = previousResetKeyRef.current !== paginationResetKey;
    previousResetKeyRef.current = paginationResetKey;

    if (shouldReset) {
      if (currentPage !== 1) {
        setCurrentPage(1);
      }
      return;
    }

    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, paginationResetKey, totalPages]);

  const paginatedMembers = useMemo(
    () => members.slice((activePage - 1) * itemsPerPage, activePage * itemsPerPage),
    [activePage, itemsPerPage, members],
  );

  // With a separate Workspace role column (see IDFE-3691), the product column
  // only lists product access.
  const hasWorkspaceRoleColumn = columns.includes('workspaceRole');

  const headerStrings: Record<Column, string> = {
    name: formatMessage(messages.colName),
    entity: formatMessage(messages.colEntity),
    product: formatMessage(
      hasWorkspaceRoleColumn ? messages.colProductAccess : messages.colProduct,
    ),
    workspaceRole: formatMessage(messages.colWorkspaceRole),
    status: formatMessage(messages.colStatus),
    lastOnline: formatMessage(messages.colLastOnline),
  };

  return (
    <Box
      flexDirection="column"
      paddingX={0}
      width="100%"
      style={containerStyle}
      data-testid="team-members-table"
    >
      <Table
        accessibilityLabel={formatMessage(messages.tableName)}
        tableLayout="fixed"
        style={tableStyle}
      >
        <TableHeader>
          <TableRow disableHoverIndicator>
            {columns.map((col, i) => (
              <TableCell
                key={col}
                as="th"
                scope="col"
                width={
                  hasWorkspaceRoleColumn && NARROW_COLUMNS.has(col)
                    ? NARROW_COLUMN_WIDTH
                    : undefined
                }
                outerSpacing={i === 0 ? firstTableCellWhitespaceStyle : tableCellWhitespaceStyle}
              >
                <Text font="label1" color="fgMuted">
                  {headerStrings[col]}
                </Text>
              </TableCell>
            ))}
            {showActions && (
              <TableCell as="th" width={64} title="" outerSpacing={lastTableCellWhitespaceStyle} />
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {paginatedMembers.map((member) => (
            <UserCell
              key={member.email}
              member={member}
              orgName={orgName}
              columns={columns}
              isRemoving={removingMemberEmails?.has(member.email) ?? false}
              onNameClick={onNameClick}
              onPendingInviteClick={onPendingInviteClick}
              onRemoveFromOrg={onRemoveFromOrg}
              onCancelInvite={onCancelInvite}
              canCancelInvite={canCancelInvite?.(member)}
              onCancelPendingRole={onCancelPendingRole}
              canCancelPendingRole={canCancelPendingRole}
              showActions={showActions}
              canRemove={canRemove}
              canEdit={canEdit}
            />
          ))}
        </TableBody>
      </Table>

      <Box justifyContent="center" style={paginationBoxStyle}>
        <Pagination
          totalPages={totalPages}
          activePage={activePage}
          onChange={setCurrentPage}
          accessibilityLabels={accessibilityLabels}
          width={450}
          justifyContent="space-between"
        />
      </Box>
    </Box>
  );
}
